import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import GroupChip from '../components/GroupChip';
import GroupFormModal from '../components/GroupFormModal';

// Turn a stored type like "secret_santa" into "Secret Santa" for display.
function prettyType(type) {
    if (!type) return '';
    return type
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [groups, setGroups] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        (async () => {
            const [g, pending] = await Promise.all([fetchGroups(), fetchInvitations()]);

            // If you're in exactly one group and have no invitations to deal with,
            // skip the dashboard and go straight into that group — unless we got
            // here via an explicit "back to dashboard" action (location.state.stay).
            const stay = location.state?.stay;
            if (!stay && g.length === 1 && pending.length === 0) {
                navigate(`/group/${g[0].id}`, { replace: true });
                return;
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchGroups = async () => {
        try {
            const response = await api.get('/groups');
            setGroups(response.data);
            return response.data;
        } catch (err) {
            setError('Failed to load groups');
            return [];
        }
    };

    const fetchInvitations = async () => {
        try {
            const response = await api.get('/invitations');
            const pending = response.data.filter((inv) => inv.status === 'pending');
            setInvitations(pending);
            return pending;
        } catch (err) {
            return [];
        }
    };

    const handleDeleteGroup = async (e, group) => {
        e.stopPropagation();
        const memberCount = group.members?.length || 0;
        const warning = memberCount > 1
            ? `\n\nIt has ${memberCount} members and will be removed for everyone.`
            : '';
        if (!window.confirm(`Delete "${group.name}"?${warning}\n\nThis cannot be undone.`)) return;

        try {
            await api.delete(`/groups/${group.id}`);
            setGroups((prev) => prev.filter((g) => g.id !== group.id));
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete group');
        }
    };

    const handleAcceptInvitation = async (token) => {
        try {
            await api.post(`/invitations/${token}/accept`);
            setSuccessMessage('Invitation accepted! Welcome to the group!');
            setTimeout(() => setSuccessMessage(''), 3000);
            fetchGroups();
            fetchInvitations();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to accept invitation');
        }
    };

    const handleDeclineInvitation = async (token) => {
        if (!window.confirm('Are you sure you want to decline this invitation?')) return;
        try {
            await api.delete(`/invitations/${token}`);
            setInvitations(invitations.filter((inv) => inv.token !== token));
            setSuccessMessage('Invitation declined');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to decline invitation');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-gray-600">Loading...</div>
            </div>
        );
    }

    return (
        <AppLayout maxWidth="max-w-7xl">
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
                )}
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">{successMessage}</div>
                )}

                {/* Pending Invitations */}
                {invitations.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Pending Invitations</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {invitations.map((invitation) => (
                                <div key={invitation.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 shadow-sm">
                                    <div className="flex items-start justify-between mb-3">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-1">{invitation.group?.name}</h3>
                                            <p className="text-sm text-gray-600">Invited by {invitation.invited_by?.name}</p>
                                        </div>
                                        <span className="text-2xl">✉️</span>
                                    </div>
                                    <div className="text-sm text-gray-600 mb-4">
                                        <p>{invitation.group?.members?.length || 0} member(s)</p>
                                        <p className="text-xs mt-1">Expires {new Date(invitation.expires_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleAcceptInvitation(invitation.token)} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
                                            Accept
                                        </button>
                                        <button onClick={() => handleDeclineInvitation(invitation.token)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md text-sm font-medium">
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Your Groups */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Your Groups</h2>
                    <button onClick={() => setShowCreateGroup(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">
                        + Create Group
                    </button>
                </div>

                {showCreateGroup && (
                    <GroupFormModal
                        group={null}
                        onClose={() => setShowCreateGroup(false)}
                        onSaved={(g) => {
                            setGroups([...groups, g]);
                            setShowCreateGroup(false);
                        }}
                    />
                )}
                {editingGroup && (
                    <GroupFormModal
                        group={editingGroup}
                        onClose={() => setEditingGroup(null)}
                        onSaved={(g) => {
                            // The update response omits members/creator — merge so the card keeps them.
                            setGroups((prev) => prev.map((x) => (x.id === g.id ? { ...x, ...g } : x)));
                            setEditingGroup(null);
                        }}
                    />
                )}

                {groups.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <p className="text-gray-500 mb-4">You're not part of any groups yet.</p>
                        <button onClick={() => setShowCreateGroup(true)} className="text-blue-600 hover:text-blue-700 font-medium">
                            Create your first group
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {groups.map((group) => {
                            const isAdmin = group.created_by_user_id === user?.id;
                            return (
                                <div
                                    key={group.id}
                                    onClick={() => navigate(`/group/${group.id}`)}
                                    className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow cursor-pointer"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            {group.icon && <span className="text-2xl" aria-hidden="true">{group.icon}</span>}
                                            <h3 className="text-xl font-semibold text-gray-900 truncate">{group.name}</h3>
                                        </div>
                                        {group.type && (
                                            <GroupChip name={prettyType(group.type)} icon={null} color={group.color} seed={group.id} />
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 mb-4">
                                        {group.members?.length || 0} member{group.members?.length !== 1 ? 's' : ''}
                                    </p>
                                    {isAdmin && (
                                        <div className="flex items-center justify-between">
                                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Admin</span>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setEditingGroup(group); }}
                                                    className="text-sm text-gray-600 hover:text-gray-900"
                                                >
                                                    Edit
                                                </button>
                                                <button onClick={(e) => handleDeleteGroup(e, group)} className="text-sm text-red-600 hover:text-red-700">
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
        </AppLayout>
    );
}
